<template>
  <div id="content-wrapper" class="cau-hinh-chuc-nang-otp otp-scope">
    <div class="otp-page-head">
      <h5 class="otp-page-title">Cấu hình chức năng OTP</h5>
      <p class="otp-page-sub">
        Bật/tắt yêu cầu xác thực OTP (step-up) cho từng chức năng nghiệp vụ. Thay đổi có hiệu lực ngay sau khi lưu.
      </p>
    </div>

    <!-- 3 toggle chức năng đã wire thật ở qlvb-be (Trình ký / Phê duyệt / Thu hồi) - xếp cùng 1 hàng ngang. -->
    <div class="toggle-row">
      <b-form-checkbox
        v-for="f in toggles"
        :key="f.key"
        v-model="policies[f.key]"
        switch
        class="toggle-item"
      >
        <span class="font-weight-bold">{{ f.label }}</span>
      </b-form-checkbox>
    </div>

    <div class="save-bar mt-3">
      <base-button
        button="Lưu cấu hình"
        @click="save"
        :disabled="isLoading"
      ></base-button>
    </div>
    <BaseLoading :is-loading="isLoading" />

    <hr class="section-divider my-4" />

    <h5 class="otp-section-title">Lịch sử thay đổi cấu hình</h5>
    <DataTable :loading="isLoadingHistory" :value="historyItems">
      <Column header="STT" headerStyle="width: 60px" bodyStyle="text-align: center">
        <template #body="slotProps">
          <span class="cell-text">{{ (req.pageIndex - 1) * req.pageSize + slotProps.index + 1 }}</span>
        </template>
      </Column>
      <Column header="Chức năng" headerStyle="width: 220px" bodyStyle="text-align: left">
        <template #body="slotProps">
          <span class="cell-text">{{ actionTypeLabel(slotProps.data.actionType) }}</span>
        </template>
      </Column>
      <Column header="Trạng thái" headerStyle="width: 120px" bodyStyle="text-align: center">
        <template #body="slotProps">
          <span class="otp-chip" :class="slotProps.data.enabled ? 'otp-chip--on' : 'otp-chip--off'">
            <span class="otp-chip__dot" aria-hidden="true"></span>
            {{ slotProps.data.enabled ? "Bật" : "Tắt" }}
          </span>
        </template>
      </Column>
      <Column header="Người thay đổi" headerStyle="width: 220px" bodyStyle="text-align: left">
        <template #body="slotProps">
          <span class="cell-text">{{ slotProps.data.changedByName || slotProps.data.changedBy }}</span>
        </template>
      </Column>
      <Column header="Thời điểm" headerStyle="width: 180px" bodyStyle="text-align: center">
        <template #body="slotProps">
          <span class="cell-text">{{ formatDate(slotProps.data.createdAt) }}</span>
        </template>
      </Column>
      <template #empty><div class="text-center">Không có dữ liệu</div></template>
      <template #loading><div class="text-center">Đang tải dữ liệu ...</div></template>
    </DataTable>

    <BasePagination
      class="mt-3"
      :current-page="req.pageIndex"
      :total-pages="totalPages"
      :total-rows="totalRows"
      @changeNum="onChangeHistoryNum($event)"
      @changePages="onChangeHistoryPages($event)"
    />
  </div>
</template>

<script>
import BaseLoading from "@/components/common/BaseLoading";
import BasePagination from "@/components/common/BasePagination";
import { setBreadcrumb } from "@/utils/commonUtils";
import { MENU_ROUTE_NAME } from "@/constants/constants";
import moment from "moment";

// UC14 Phase 2: màn QTHT cấu hình step-up theo 4 chức năng (KySo/PheDuyet/ThuHoi/CapQuyen) + lịch sử.
// Contract common-be Phase 1 (đã cook): GET/PUT trả/nhận FLAT bool {kySo, pheDuyet, thuHoi, capQuyen}
// (KHÔNG phải mảng policies[] như bản nháp phase-02 cũ). PUT nhận từng field OPTIONAL (null = giữ nguyên).
// History: actionType BE có [JsonStringEnumConverter] nhưng runtime có thể lệch (global serializer chưa
// bật enum-string) - map cả string enum lẫn int enum-value để chịu 2 định dạng, tránh in raw số ra UI.
const ACTION_TYPE_LABEL = {
  KySo: "Ký số",
  PheDuyet: "Phê duyệt",
  ThuHoi: "Thu hồi",
  CapQuyen: "Cấp quyền",
  TrinhKy: "Trình ký",
  0: "Ký số",
  1: "Phê duyệt",
  2: "Thu hồi",
  3: "Cấp quyền",
  4: "Trình ký",
};

export default {
  name: "TrangCauHinhChucNangOtp",
  components: { BaseLoading, BasePagination },
  data() {
    return {
      isLoading: false,
      policies: {
        kySo: false,
        pheDuyet: false,
        thuHoi: false,
        capQuyen: false,
        trinhKy: false,
      },
      // Chỉ hiển thị 3 chức năng đã wire thật ở qlvb-be (Trình ký / Phê duyệt / Thu hồi).
      // KySo + CapQuyen chưa apply code qlvb-be -> ẨN khỏi UI. Field vẫn tồn tại trong policies{} để
      // PUT gửi đủ contract (BE nhận optional-null giữ nguyên; ta gửi default false để không ảnh hưởng).
      toggles: [
        { key: "trinhKy", label: "Trình ký" },
        { key: "pheDuyet", label: "Phê duyệt" },
        { key: "thuHoi", label: "Thu hồi" },
      ],
      isLoadingHistory: false,
      historyItems: [],
      totalPages: 0,
      totalRows: 0,
      req: {
        pageIndex: 1,
        pageSize: 10,
      },
    };
  },
  created() {
    setBreadcrumb(MENU_ROUTE_NAME.QUAN_TRI_HE_THONG.CAU_HINH_CHUC_NANG_OTP);
    this.loadPolicies();
    this.loadHistory();
  },
  methods: {
    actionTypeLabel(actionType) {
      return ACTION_TYPE_LABEL[actionType] || actionType;
    },
    formatDate(d) {
      return d ? moment(d).format("DD/MM/YYYY HH:mm:ss") : "";
    },
    loadPolicies() {
      this.isLoading = true;
      this.$store
        .dispatch("common/getStepUpPolicies")
        .then((res) => {
          this.isLoading = false;
          if (res.succeeded && res.data) {
            this.policies = { ...this.policies, ...res.data };
          }
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
    save() {
      this.isLoading = true;
      this.$store
        .dispatch("common/updateStepUpPolicies", { ...this.policies })
        .then((res) => {
          this.isLoading = false;
          if (res.succeeded) {
            if (res.data) this.policies = { ...this.policies, ...res.data };
            this.$toasted.clear();
            this.$toasted.global.showSuccessMessage({
              message: "Lưu cấu hình chức năng OTP thành công",
            });
            this.loadHistory();
          }
        })
        .catch((error) => {
          this.isLoading = false;
          this.$toasted.clear();
          this.$toasted.global.showErrorMessage({
            message: (error && error.data && error.data.message) || "Lưu cấu hình thất bại",
          });
        });
    },
    loadHistory() {
      this.isLoadingHistory = true;
      this.$store
        .dispatch("common/searchStepUpPolicyHistory", { ...this.req })
        .then((res) => {
          this.isLoadingHistory = false;
          if (res.succeeded && res.data) {
            this.historyItems = res.data.items || [];
            this.totalRows = res.data.totalCount || 0;
            this.totalPages = res.data.totalPage || 0;
          }
        })
        .catch(() => {
          this.isLoadingHistory = false;
        });
    },
    onChangeHistoryNum(pageSize) {
      this.req.pageIndex = 1;
      this.req.pageSize = pageSize;
      this.loadHistory();
    },
    onChangeHistoryPages(page) {
      this.req.pageIndex = page;
      this.loadHistory();
    },
  },
};
</script>

<style lang="scss" scoped>
// Reskin OTP (Phase 2): overlay token --otp-* + typography + chip trạng thái. Layout/logic giữ nguyên.
// Layout full-width theo #content-wrapper; toggle 3 chức năng nằm 1 hàng ngang, nút Lưu ngay dưới.
.cau-hinh-chuc-nang-otp {
  font-family: var(--otp-sans);
  font-size: 14px;
  color: var(--otp-ink);

  .otp-page-head {
    margin-bottom: 20px;
  }
  .otp-page-title,
  .otp-section-title {
    font-family: var(--otp-display);
    font-weight: 600;
    color: var(--otp-ink);
  }
  .otp-page-title {
    font-size: 20px;
    margin: 0 0 6px;
  }
  .otp-section-title {
    font-size: 17px;
    margin: 0 0 16px;
  }
  .otp-page-sub {
    color: var(--otp-muted);
    font-size: 13.5px;
    margin: 0;
  }

  // Chip trạng thái Bật/Tắt: nền + chấm + chữ (màu+icon+chữ đạt WCAG).
  .otp-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 10px;
    border-radius: 20px;
    font-size: 12.5px;
    font-weight: 600;
    line-height: 1.6;
    .otp-chip__dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: currentColor;
      flex: none;
    }
    &--on {
      background: var(--otp-success-bg);
      color: var(--otp-success);
    }
    &--off {
      background: var(--otp-danger-bg);
      color: var(--otp-danger);
    }
  }

  // 3 toggle chức năng trên 1 hàng ngang (desktop), wrap xuống khi màn hẹp.
  .toggle-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 32px;
    margin-bottom: 16px;
    .toggle-item {
      margin: 0;
    }
  }

  // Nút Lưu cấu hình - clearfix xử lý BaseButton float-left, đẩy nút không dính bảng bên dưới.
  .save-bar {
    &::after {
      content: "";
      display: table;
      clear: both;
    }
  }

  // Line ngăn cách rõ giữa nhóm cấu hình và bảng lịch sử (đồng bộ với CauHinhBaoMatOtp).
  .section-divider {
    border: 0;
    border-top: 1px solid var(--otp-border);
    opacity: 1;
  }

  // Cell text mặc định: ép về ink token, tránh style link màu xanh mặc định leak vào body cells.
  ::v-deep .cell-text {
    color: var(--otp-ink);
    text-decoration: none;
  }
}
</style>
